import React, { useState, useEffect, useCallback } from 'react';
import { deviceAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Server, Plus, Search } from 'lucide-react';

function AddDeviceModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    serialNumber: '', macAddress: '', model: '', manufacturer: '',
    deviceType: 'onu', olt: { name: '', ip: '', ponPort: '' },
    splitter: { name: '', location: '', port: '' },
    notes: '', purchaseDate: '', purchasePrice: '', vendor: ''
  });
  const [loading, setLoading] = useState(false);
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setNested = (parent, key, val) => setForm(f => ({ ...f, [parent]: { ...f[parent], [key]: val } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await deviceAPI.create(form);
      toast.success('Device added to inventory');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add device');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h3>Add Device to Inventory</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Serial Number <span className="required">*</span></label>
                <input className="form-control font-mono" value={form.serialNumber} onChange={e => set('serialNumber', e.target.value.toUpperCase())} required />
              </div>
              <div className="form-group">
                <label className="form-label">MAC Address <span className="required">*</span></label>
                <input className="form-control font-mono" value={form.macAddress} onChange={e => set('macAddress', e.target.value.toUpperCase())} required placeholder="XX:XX:XX:XX:XX:XX" />
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Device Type</label>
                <select className="form-control" value={form.deviceType} onChange={e => set('deviceType', e.target.value)}>
                  <option value="onu">ONU</option>
                  <option value="olt">OLT</option>
                  <option value="router">Router</option>
                  <option value="set_top_box">Set Top Box</option>
                  <option value="switch">Switch</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Model <span className="required">*</span></label>
                <input className="form-control" value={form.model} onChange={e => set('model', e.target.value)} required />
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Manufacturer</label>
                <input className="form-control" value={form.manufacturer} onChange={e => set('manufacturer', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Vendor</label>
                <input className="form-control" value={form.vendor} onChange={e => set('vendor', e.target.value)} />
              </div>
            </div>

            <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--text-muted)', margin: '12px 0 8px', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              OLT / Network Info
            </div>
            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">OLT Name</label>
                <input className="form-control" value={form.olt.name} onChange={e => setNested('olt', 'name', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">OLT IP</label>
                <input className="form-control font-mono" value={form.olt.ip} onChange={e => setNested('olt', 'ip', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">PON Port</label>
                <input className="form-control" value={form.olt.ponPort} onChange={e => setNested('olt', 'ponPort', e.target.value)} />
              </div>
            </div>
            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">Splitter Name</label>
                <input className="form-control" value={form.splitter.name} onChange={e => setNested('splitter', 'name', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Splitter Location</label>
                <input className="form-control" value={form.splitter.location} onChange={e => setNested('splitter', 'location', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Splitter Port</label>
                <input type="number" className="form-control" value={form.splitter.port} onChange={e => setNested('splitter', 'port', e.target.value)} />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Purchase Date</label>
                <input type="date" className="form-control" value={form.purchaseDate} onChange={e => set('purchaseDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Purchase Price (₹)</label>
                <input type="number" className="form-control" value={form.purchasePrice} onChange={e => set('purchasePrice', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <Server size={14} /> {loading ? 'Adding...' : 'Add Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DevicesPage() {
  const [devices, setDevices] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({ page: 1, status: '', deviceType: '', search: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 20 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await deviceAPI.getAll(params);
      setDevices(data.data);
      setPagination(data.pagination);
    } catch { toast.error('Failed to load devices'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const STATUS_COLOR = { in_stock: 'info', installed: 'success', faulty: 'danger', returned: 'warning', retired: 'suspended' };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Device Inventory</h2>
          <p className="text-muted text-small">{pagination.total || 0} devices · ONU/OLT/Router management</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={15} /> Add Device
        </button>
      </div>

      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search serial, MAC, model..."
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        </div>
        <select className="form-control" style={{ width: 140 }} value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All Status</option>
          <option value="in_stock">In Stock</option>
          <option value="installed">Installed</option>
          <option value="faulty">Faulty</option>
          <option value="retired">Retired</option>
        </select>
        <select className="form-control" style={{ width: 130 }} value={filters.deviceType}
          onChange={e => setFilters(f => ({ ...f, deviceType: e.target.value, page: 1 }))}>
          <option value="">All Types</option>
          <option value="onu">ONU</option>
          <option value="olt">OLT</option>
          <option value="router">Router</option>
          <option value="set_top_box">Set Top Box</option>
        </select>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : devices.length === 0 ? (
            <div className="empty-state"><Server size={32} /><h3>No devices found</h3><p>Add devices to your inventory.</p></div>
          ) : (
            <table>
              <thead>
                <tr><th>Serial Number</th><th>MAC</th><th>Type / Model</th><th>OLT / Port</th><th>Customer</th><th>Status</th><th>Added</th></tr>
              </thead>
              <tbody>
                {devices.map(d => (
                  <tr key={d._id}>
                    <td className="font-mono" style={{ fontSize: '.82rem', fontWeight: 600 }}>{d.serialNumber}</td>
                    <td className="font-mono" style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{d.macAddress}</td>
                    <td>
                      <div style={{ textTransform: 'capitalize', fontSize: '.82rem', fontWeight: 500 }}>{d.deviceType?.replace(/_/g, ' ')}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{d.manufacturer} {d.model}</div>
                    </td>
                    <td style={{ fontSize: '.78rem' }}>
                      {d.olt?.name ? <><div>{d.olt.name}</div><div style={{ color: 'var(--text-muted)' }}>Port: {d.olt.ponPort}</div></> : '—'}
                    </td>
                    <td style={{ fontSize: '.82rem' }}>
                      {d.customer ? <><div style={{ fontWeight: 500 }}>{d.customer.name}</div><div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{d.customer.customerId}</div></> : <span style={{ color: 'var(--text-light)' }}>—</span>}
                    </td>
                    <td><span className={`badge badge-${STATUS_COLOR[d.status] || 'info'}`} style={{ textTransform: 'capitalize' }}>{d.status?.replace(/_/g, ' ')}</span></td>
                    <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{d.createdAt ? format(new Date(d.createdAt), 'dd/MM/yy') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="pagination">
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <span style={{ fontSize: '.8rem', padding: '0 8px' }}>{pagination.page} / {pagination.pages}</span>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>

      {/* ACS Future Banner */}
      <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--bg)', border: '1px dashed var(--border)', borderRadius: 8, fontSize: '.82rem', color: 'var(--text-muted)' }}>
        <strong>🔮 Future:</strong> ACS / TR-069 integration will enable remote ONU reboot, WiFi password change, firmware upgrade, and diagnostics from this screen. The database schema and API routes are already reserved.
      </div>

      {showModal && <AddDeviceModal onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); load(); }} />}
    </div>
  );
}
